import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const AdminAuthContext = createContext();

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error("useAdminAuth must be used within AdminAuthProvider");
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [client, setClient] = useState(null);
  const [gameAccess, setGameAccess] = useState(["all"]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Initialize auth state from localStorage
  useEffect(() => {
    const storedToken = localStorage.getItem("adminToken");
    const storedClient = localStorage.getItem("adminClient");
    const storedGameAccess = localStorage.getItem("adminGameAccess");

    if (storedToken && storedClient) {
      setToken(storedToken);
      setClient(storedClient);
      if (storedGameAccess) {
        try {
          const parsed = JSON.parse(storedGameAccess);
          setGameAccess(Array.isArray(parsed) && parsed.length > 0 ? parsed : ["all"]);
        } catch (_) {
          setGameAccess(["all"]);
        }
      }
      setIsAuthenticated(true);
    }
    setLoading(false);
  }, []);

  // Generate auth headers
  const getAuthHeaders = useCallback(() => {
    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  }, [token]);

  // Login function
  const login = useCallback(async (password) => {
    try {
      // Prevent stale access values from prior sessions before new login resolves.
      localStorage.removeItem("adminGameAccess");
      const response = await axios.post("/api/auth/login", { password });
      const {
        token: newToken,
        client: newClient,
        gameAccess: newGameAccess = ["all"],
      } = response.data;

      // Store in localStorage
      localStorage.setItem("adminToken", newToken);
      localStorage.setItem("adminClient", newClient);
      const normalizedAccess =
        Array.isArray(newGameAccess) && newGameAccess.length > 0
          ? newGameAccess
          : ["all"];
      localStorage.setItem("adminGameAccess", JSON.stringify(normalizedAccess));

      // Update state
      setToken(newToken);
      setClient(newClient);
      setGameAccess(normalizedAccess);
      setIsAuthenticated(true);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || "Login failed",
      };
    }
  }, []);

  // Logout function
  const logout = useCallback(() => {
    // Clear localStorage
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminClient");
    localStorage.removeItem("adminGameAccess");

    // Clear state
    setToken(null);
    setClient(null);
    setGameAccess(["all"]);
    setIsAuthenticated(false);

    // Redirect to login
    navigate("/admin");
  }, [navigate]);

  // Handle 401 errors globally
  const handleAuthError = useCallback(
    (error) => {
      if (error.response?.status === 401) {
        logout();
        return true; // Indicates this was an auth error
      }
      return false;
    },
    [logout]
  );

  // Protected API call wrapper
  const makeAuthenticatedRequest = useCallback(
    async (requestFn) => {
      try {
        return await requestFn(getAuthHeaders());
      } catch (error) {
        if (handleAuthError(error)) {
          throw new Error("Authentication failed. Please log in again.");
        }
        throw error;
      }
    },
    [getAuthHeaders, handleAuthError]
  );

  // Require authentication (redirect if not authenticated)
  const requireAuth = useCallback(() => {
    if (!loading && !isAuthenticated) {
      navigate("/admin");
      return false;
    }
    return true;
  }, [loading, isAuthenticated, navigate]);

  const value = {
    // State
    token,
    client,
    gameAccess,
    isAuthenticated,
    loading,

    // Methods
    login,
    logout,
    getAuthHeaders,
    handleAuthError,
    makeAuthenticatedRequest,
    requireAuth,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};
